import { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { FeedbackReportKind } from "@shared/types/feedback_reports.ts";
import { useAsyncHandler } from "../../hooks/useAsyncHandler";
import { sendFeedbackReport } from "../../lib/claimsApi";
import { asInternalPath } from "../../lib/internal_path";

const APP_VERSION = (import.meta.env.VITE_APP_VERSION as string | undefined) ?? undefined;

/**
 * Drives the account's feedback form: the kind and message, the result it is about (named by the
 * link that opened the form, and removable) and sending. Each form carries one
 * report id, so sending twice stores one report.
 */
export function useAccountFeedback() {
  const [searchParams] = useSearchParams();
  const from = asInternalPath(searchParams.get("from"));
  const [kind, setKind] = useState<FeedbackReportKind>("bug");
  const [message, setMessage] = useState("");
  const [verificationId, setVerificationId] = useState(searchParams.get("verification") ?? "");
  const [sent, setSent] = useState(false);
  const reportId = useRef(crypto.randomUUID());

  const {
    loading: sending,
    error,
    handle: send,
  } = useAsyncHandler(async () => {
    await sendFeedbackReport({
      id: reportId.current,
      kind,
      message: message.trim(),
      page: from ?? undefined,
      app_version: APP_VERSION,
      user_event_verification_id: verificationId || undefined,
    });
    setSent(true);
  });

  function sendAnother() {
    reportId.current = crypto.randomUUID();
    setMessage("");
    setVerificationId("");
    setSent(false);
  }

  return {
    kind,
    setKind,
    message,
    setMessage,
    verificationId,
    clearVerification: () => setVerificationId(""),
    canSend: message.trim().length > 0 && !sending,
    send,
    sending,
    error,
    sent,
    sendAnother,
  };
}
